import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {Icon} from '@iconify/react';
import {useSelector} from 'react-redux';
import {IChat, IElementChatbot} from '../../../../incase-domain/elements/chatbot';
import {getAnswers, initChat} from '../../../../service/chatbot.service';
import Button from '../../../../incase-domain/components/button/Button';
import InputWithCharacterCounter from '../../../../incase-domain/components/input/InputWithCharacterCounter';
import {runFileIdSelector} from '../../../../store/selectors/runfile.selectors';

interface ChatbotElementComponentProps {
    element: IElementChatbot;
}

function ChatbotElementComponent({element}: ChatbotElementComponentProps): ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const runFileId = useSelector(runFileIdSelector);

    const [message, setMessage] = useState<string>('');
    const [chat, setChat] = useState<IChat | null>(null);
    const [error, setError] = useState<boolean>(false);

    const scrollToTheBottomOfMsgContainer = (): void => {
        if (containerRef.current) {
            setTimeout(() => {
                containerRef.current?.scrollTo({top: containerRef.current.scrollHeight, behavior: 'smooth'});
            }, 100);
        }
    };

    const {mutate: startChat} = useMutation(() => initChat(element.id, runFileId), {
        onSuccess: (data) => {
            setChat(data);
        },
    });

    const {mutate: sendMessage, isLoading} = useMutation((msg: string) => getAnswers(chat?._id || '', msg), {
        onSuccess: (data) => {
            setChat(data);
            setMessage('');
            scrollToTheBottomOfMsgContainer();
        },
        onError: () => {
            setError(true);
            chat && setChat({...chat, suggestions: []});
        },
    });

    useEffect(() => {
        if (element?.id) {
            startChat();
        }
        scrollToTheBottomOfMsgContainer();
    }, [element?.id, startChat]);

    const handleSendMessage = (msg: string): void => {
        if (!msg.trim() || isLoading || !chat) return;
        setChat({...chat, chatHistory: [...(chat?.chatHistory || []), msg]});
        setMessage('');
        setError(false);
        sendMessage(msg);
        scrollToTheBottomOfMsgContainer();
    };

    return (
        <div className="c-chatbot">
            <div className="c-chatbot__header">
                <Icon icon="solar:user-bold" className="c-chatbot__header_icon" />
                {element?.header}
            </div>

            <div className="c-chatbot__container" ref={containerRef}>
                {chat?.chatHistory?.map((msg, index) => (
                    <div
                        key={index}
                        className={`c-chatbot__container_message c-chatbot__container_message--${index % 2 === 0 ? 'bot' : 'user'}`}
                    >
                        {msg}
                    </div>
                ))}

                {!isLoading &&
                    chat?.suggestions?.map((suggestion, index) => (
                        <div
                            key={index}
                            className="c-chatbot__container_message c-chatbot__container_message--suggestion"
                            onClick={() => handleSendMessage(suggestion)}
                        >
                            {suggestion}
                        </div>
                    ))}

                {isLoading && (
                    <div
                        className={`c-chatbot__container_message 
                                    c-chatbot__container_message--bot 
                                    c-chatbot__container_message--bot-loading`}
                    >
                        <Icon icon="svg-spinners:3-dots-bounce" />
                    </div>
                )}

                {error && (
                    <div className="c-chatbot__container_error">
                        An error occured.
                        <span onClick={() => handleSendMessage(chat?.chatHistory.pop() || '')}>Try again</span>
                    </div>
                )}
            </div>

            <div className="c-chatbot__input" onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(message)}>
                <InputWithCharacterCounter
                    value={message}
                    onChange={(val) => setMessage(val)}
                    placeholder={isLoading ? '...' : 'Send message...'}
                    disabled={isLoading || error}
                    trailing={
                        <Button
                            variant="ghost"
                            trailing="solar:map-arrow-right-bold-duotone"
                            onClick={() => handleSendMessage(message)}
                            disabled={isLoading}
                        />
                    }
                />
            </div>
        </div>
    );
}

export default ChatbotElementComponent;
